import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Sparkles, BookOpen, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../lib/usePlan';
import { supabase } from '../lib/supabaseClient';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type Kind = 'note' | 'prompt' | 'lesson_share';

interface Item {
  id: string;
  team_id: string;
  created_by: string;
  kind: Kind;
  title: string;
  body: string;
  lesson_path: string | null;
  created_at: string;
  updated_at: string;
  author_name?: string | null;
}

const TABS: { key: Kind; label: string; icon: typeof FileText }[] = [
  { key: 'note', label: 'Shared notes', icon: FileText },
  { key: 'prompt', label: 'Saved prompts', icon: Sparkles },
  { key: 'lesson_share', label: 'Lessons', icon: BookOpen },
];

export default function TeamWorkspacePage() {
  const { user } = useAuth();
  const { activeTeam } = usePlan();
  const [tab, setTab] = useState<Kind>('note');
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [lessonPath, setLessonPath] = useState('');

  useDocumentMeta({ title: 'Team workspace — PromptGPT', description: 'Shared notes, prompts, and lessons' });

  const teamId = activeTeam?.team_id;

  const load = useCallback(async () => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('team_workspace_items')
      .select('id, team_id, created_by, kind, title, body, lesson_path, created_at, updated_at')
      .eq('team_id', teamId)
      .eq('kind', tab)
      .order('updated_at', { ascending: false });

    if (data && data.length > 0) {
      const ids = Array.from(new Set((data as Item[]).map((i) => i.created_by)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', ids);
      const map = new Map<string, string>();
      (profiles || []).forEach((p: { id: string; full_name: string }) => map.set(p.id, p.full_name));
      setItems((data as Item[]).map((i) => ({ ...i, author_name: map.get(i.created_by) || null })));
    } else {
      setItems([]);
    }
    setLoading(false);
  }, [teamId, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setComposing(false);
    setTitle('');
    setBody('');
    setLessonPath('');
  };

  const create = async () => {
    if (!teamId || !user) return;
    if (title.trim().length < 1) {
      toast.error('Add a title');
      return;
    }
    const { error } = await supabase.from('team_workspace_items').insert({
      team_id: teamId,
      created_by: user.id,
      kind: tab,
      title: title.trim(),
      body: body.trim(),
      lesson_path: tab === 'lesson_share' ? lessonPath.trim() || null : null,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Saved');
    reset();
    await load();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this item?')) return;
    const { error } = await supabase.from('team_workspace_items').delete().eq('id', id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await load();
  };

  if (!activeTeam) {
    return (
      <div className="container-app py-16">
        <div className="max-w-xl mx-auto bg-white border border-ink-100 rounded-2xl p-10 text-center shadow-soft">
          <div className="w-12 h-12 rounded-full bg-accent-100 grid place-items-center mx-auto mb-4">
            <Users className="w-6 h-6 text-accent-700" />
          </div>
          <h1 className="font-display text-3xl mb-2">Join a team to use the workspace</h1>
          <p className="text-ink-600 mb-6">Start a team or accept an invite to share notes, prompts, and lessons.</p>
          <Link to="/pricing" className="btn-primary inline-flex">See team pricing</Link>
        </div>
      </div>
    );
  }

  const placeholder = {
    note: { title: 'What did we learn this week?', body: 'A short markdown note your team can read and reply to.' },
    prompt: { title: 'A reusable prompt for our team', body: 'Paste the prompt body. Add notes about when to use it.' },
    lesson_share: { title: 'Lesson everyone is doing this week', body: 'Why this lesson, and how it ties to our goal.' },
  }[tab];

  return (
    <div className="container-app py-10 md:py-14 max-w-5xl">
      <div className="flex items-center gap-2 text-sm text-ink-500 mb-6">
        <Link to="/team" className="hover:text-ink-700">Team</Link>
        <span>/</span>
        <span className="text-ink-700">Workspace</span>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">{activeTeam.team_name}</h1>
          <p className="text-ink-600 text-sm mt-1">A shared space for your team's notes, prompts, and lesson plans.</p>
        </div>
        {!composing && (
          <button onClick={() => setComposing(true)} className="btn-primary">
            <Plus className="w-4 h-4" /> New {TABS.find((t) => t.key === tab)?.label.toLowerCase().replace(/s$/, '')}
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-6 border-b border-ink-100">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              reset();
            }}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition ${
              tab === t.key
                ? 'border-ink-900 text-ink-900 font-medium'
                : 'border-transparent text-ink-500 hover:text-ink-700'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {composing && (
        <div className="bg-white border border-ink-100 rounded-2xl shadow-soft p-6 mb-6">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={placeholder.title}
            className="w-full px-3 py-2 mb-3 font-display text-2xl border-b border-ink-100 focus:outline-none"
            autoFocus
          />
          {tab === 'lesson_share' && (
            <input
              value={lessonPath}
              onChange={(e) => setLessonPath(e.target.value)}
              placeholder="/skill-paths/some-path"
              className="w-full px-3 py-2 mb-3 text-sm border border-ink-100 rounded-lg focus:outline-none focus:border-ink-300"
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={placeholder.body}
            rows={8}
            className="w-full px-3 py-2 text-sm border border-ink-100 rounded-lg focus:outline-none focus:border-ink-300 resize-y"
          />
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={reset} className="text-ink-500 hover:text-ink-700 px-4 py-2">
              Cancel
            </button>
            <button onClick={create} className="btn-primary">
              Save
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-ink-500">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-ink-200 rounded-2xl p-10 text-center">
          <p className="text-ink-600 text-sm">Nothing here yet. Be the first to add something for your team.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-ink-100 rounded-2xl p-5 shadow-soft">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-lg mb-1">{it.title}</h3>
                  {it.lesson_path && (
                    <Link to={it.lesson_path} className="text-xs text-accent-700 hover:underline">
                      {it.lesson_path}
                    </Link>
                  )}
                  <p className="text-sm text-ink-700 mt-2 whitespace-pre-wrap">{it.body}</p>
                  <div className="text-xs text-ink-500 mt-3">
                    By {it.author_name || 'Unknown'} · {new Date(it.updated_at).toLocaleDateString()}
                  </div>
                </div>
                {it.created_by === user?.id && (
                  <button
                    onClick={() => remove(it.id)}
                    className="text-ink-400 hover:text-coral-600 p-1.5"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
